import React, { useState, useEffect } from 'react'
import { Eye, EyeOff, Shuffle } from 'lucide-react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import Select from '../ui/Select'
import { useToast } from '../Toast'
import { 
  UsuarioFormData, 
  tiposUsuario, 
  validarEmail, 
  validarSenha, 
  gerarSenhaAleatoria 
} from '../../services/usuarios'
import { User } from '../../types'

interface UsuarioFormProps {
  usuario?: User
  onSubmit: (data: UsuarioFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

const UsuarioForm: React.FC<UsuarioFormProps> = ({
  usuario,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const { success, error } = useToast()
  const isEdit = !!usuario

  const [formData, setFormData] = useState<UsuarioFormData>({
    nome: usuario?.nome || '',
    email: usuario?.email || '',
    password: '',
    tipo: usuario?.tipo || 'TECNICO',
    ativo: usuario?.ativo ?? true
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)

  const handleChange = (field: keyof UsuarioFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const gerarSenha = () => {
    const novaSenha = gerarSenhaAleatoria()
    setFormData(prev => ({ ...prev, password: novaSenha }))
    setShowPassword(true)
    success('Senha gerada automaticamente')
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Validar nome
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório'
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres'
    }

    // Validar email
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório'
    } else if (!validarEmail(formData.email)) {
      newErrors.email = 'Email inválido'
    }

    // Validar senha (obrigatória para criação, opcional para edição)
    if (!isEdit && !formData.password) {
      newErrors.password = 'Senha é obrigatória'
    } else if (formData.password) {
      const validacaoSenha = validarSenha(formData.password)
      if (!validacaoSenha.valida) {
        newErrors.password = validacaoSenha.mensagem || 'Senha inválida'
      }
    }

    // Validar tipo
    if (!formData.tipo) {
      newErrors.tipo = 'Tipo de utilizador é obrigatório'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      // Preparar dados para envio
      const submitData: UsuarioFormData = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        tipo: formData.tipo,
        ativo: formData.ativo
      }

      // Incluir senha apenas se foi fornecida
      if (formData.password) {
        submitData.password = formData.password
      }

      await onSubmit(submitData)
    } catch (error: any) {
      console.error('Erro ao salvar utilizador:', error)
      // Erro já é tratado pelo onError da mutation
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Nome */}
      <Input
        label="Nome completo"
        value={formData.nome}
        onChange={(e) => handleChange('nome', e.target.value)}
        error={errors.nome}
        placeholder="Digite o nome completo"
        required
      />

      {/* Email */}
      <Input
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => handleChange('email', e.target.value)}
        error={errors.email}
        placeholder="utilizador@exemplo.com"
        required
      />

      {/* Senha */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Senha {!isEdit && <span className="text-red-500">*</span>}
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={gerarSenha}
            leftIcon={<Shuffle className="h-3 w-3" />}
          >
            Gerar
          </Button>
        </div>
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            error={errors.password}
            placeholder={isEdit ? 'Deixe vazio para manter a senha atual' : 'Digite a senha'}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />
        </div>
        {!isEdit && (
          <p className="text-xs text-gray-500">
            A senha deve ter pelo menos 6 caracteres, incluindo maiúscula, minúscula e número
          </p>
        )}
      </div>

      {/* Tipo de Utilizador */}
      <Select
        label="Tipo de Utilizador"
        value={formData.tipo}
        onChange={(e) => handleChange('tipo', e.target.value)}
        error={errors.tipo}
        options={tiposUsuario.map(tipo => ({
          value: tipo.value,
          label: `${tipo.icon} ${tipo.label}`
        }))}
        required
      />

      {/* Status (apenas para edição) */}
      {isEdit && (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="ativo"
            checked={formData.ativo}
            onChange={(e) => handleChange('ativo', e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="ativo" className="text-sm font-medium text-gray-700">
            Utilizador ativo
          </label>
        </div>
      )}

      {/* Botões */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          isLoading={isLoading}
          loadingText={isEdit ? 'Guardando...' : 'Criando...'}
        >
          {isEdit ? 'Guardar Alterações' : 'Criar Utilizador'}
        </Button>
      </div>
    </form>
  )
}

export default UsuarioForm